import { Footer } from "@phoenix/site/components/footer";
import { Header } from "@phoenix/site/components/header";
import { getApi, useApi } from "@core/api";
import { MiniMenu } from "@site/components/MiniMenu/MiniMenu";
import type { LinksFunction, LoaderFunctionArgs, MetaFunction } from "@remix-run/node";
import { redirect } from "@remix-run/node";
import { Outlet, ShouldRevalidateFunction, json, useLoaderData, useLocation, useMatches } from "@remix-run/react";
import HomepageTabs from "@site/components/HomepageTabs";
import { EmulationBanner } from "@site/components/Emulation/EmulationBanner";
import { AccountLocationBanner } from "@site/components/AccountContext/AccountLocationBanner";
import ExtensionPoint from "@core/components/PipelineComponents/ExtensionPoint";
import { LoginRouteConfigSection, SiteConfigSection } from "@/apidtos";
import { buildLoginRedirectUrl, isPublicAuthPath } from "@/overrides/site/utils/site-auth-redirect";
import { getConfig, minutes } from "@core/helpers";
import { useSitePluginConfig } from "@site/hooks/useSitePluginConfig";
import { useQuery } from "@tanstack/react-query";
import { AgreementUserGates } from "@/components/agreements/AgreementUserGates";
import { SiteNavProvider } from "@/overrides/site/SiteNavContext";
import { resolveSiteNav, type SiteNavLink } from "@/overrides/site/siteNav";

export const handle = {
  breadcrumb: { key: "ui.home", fallback: "Home" }
};

export const links: LinksFunction = () => [
  { rel: "stylesheet", href: "/spg-theme-assets/spg-theme-css.css" },
];

export const meta: MetaFunction = (args) => {
  const config = getConfig(args);
  const siteSettings = config.Site as SiteConfigSection;
  return [
    { title: siteSettings?.SiteName ?? "Phoenix" },
    { name: "description", content: "Phoenix" },
  ];
};

export async function loader(args: LoaderFunctionArgs) {
  const api = await getApi(args.request);
  const config = await api["/config"]();
  const loginConfig = config.Login as LoginRouteConfigSection;
  const requestUrl = new URL(args.request.url);

  const [ user, pageTree, isAdmin, siteNavResponse
  ] = await Promise.all([
    api["/auth/current"](),
    api.GetPagesTree(),
    api["/auth/role-check/{role}"]("Global Admin"),
    api.api.get("/spg/site-nav").then((r) => r.data).catch(() => null),
  ]);
  const siteNav: SiteNavLink[] = resolveSiteNav(siteNavResponse);

  if (
    loginConfig?.RequireLoginForSiteAccess
    && !user?.Id
    && !isPublicAuthPath(requestUrl.pathname)
  ) {
    return redirect(buildLoginRedirectUrl(requestUrl));
  }

  const response = json(
    {
      user,
      pageTree,
      isAdmin,
      siteNav,
    },
    {
      headers: {
        // Prevent CDN or browser caching the result of the auth call
        "Cache-Control": "no-store, must-revalidate",
      }
    }
  );
  for (const cookie of api.cookies) {
    response.headers.append("Set-Cookie", cookie);
  }
  return response;
}

export const shouldRevalidate: ShouldRevalidateFunction = (args) => {
  // Only revalidate the loader for the site's root if something auth-related occurs
  // This prevents reloading the current user every time an action submits
  return args.formAction?.includes("auth") ?? false;
}

export default function Index() {
  const api = useApi();
  const { user, siteNav } = useLoaderData<typeof loader>();
  const location = useLocation();
  const sitePluginSettings = useSitePluginConfig();
  const matches = useMatches();
  const childData = matches[matches.length - 1]?.data as any;
  const hideLayout = childData?.hideLayout === true;
  const isPrintRoute = location.pathname.startsWith("/print");
  // query on the front end because the EmulationBanner uses the same query
  // and does not rely on a loader
  const { data: currentUserCanEmulate } = useQuery({
      queryKey: ["emulation", "currentUserIsAble"],
      queryFn: () => api["/emulation/current-user/is-allowed"](),
      staleTime: minutes(1),
  });

  if (isPrintRoute) {
    return <Outlet />;
  }

  return (
    <SiteNavProvider value={siteNav}>
    <div className="min-h-screen flex flex-col">
      {!hideLayout && user && currentUserCanEmulate?.IsSuccess && <div className="print:hidden"><EmulationBanner /></div>}
      {!hideLayout && user && <AccountLocationBanner />}
      {!hideLayout && (
        <Header menu={<Menu />}>
          {sitePluginSettings.AddHomepageTabs && <HomepageTabs />}
        </Header>
      )}
      {!hideLayout && <ExtensionPoint name="site.layout.header.below" />}
      {user?.Id ? <AgreementUserGates scopes={["user", "provider"]} /> : null}
      <main>
        <div>
          <Outlet />
        </div>
      </main>
      {!hideLayout && sitePluginSettings.ShowFooter && (
        <footer className="mt-auto print:hidden">
          <Footer />
        </footer>
      )}
    </div>
    </SiteNavProvider>
  );
}

const Menu = () => {
  return (
    <div className="flex flex-col md:flex-row gap-4 w-full md:w-auto items-stretch md:items-center md:justify-end">
      <ExtensionPoint name="_site.route.menu.quoteCart" />
      <ExtensionPoint name="_site.route.menu.shoppingCart" />
      <ExtensionPoint name="_site.route.shoppingCart.after" />
      <ExtensionPoint name="_site.route.minimenu" />
      <ExtensionPoint name="site.user.menu.dropdown" />
      <MiniMenu />
    </div>
  );
};
